import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Box,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface CategoryRow {
	id: number;
	name: string;
	activityTonnes: number;
	spendTonnes: number;
	totalTonnes: number;
	methods: string[];
}

/** 15-category GHG Protocol Scope 3 breakdown (Enable tier). */
const Scope3Categories: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [rows, setRows] = useState<CategoryRow[]>([]);
	const [covered, setCovered] = useState(0);
	const [year, setYear] = useState(String(new Date().getFullYear() - 1));

	const load = useCallback(async () => {
		const token = await getAccessTokenSilently();
		const res = await fetch(
			`${API}/api/scope3/categories?year=${encodeURIComponent(year)}`,
			{ headers: { Authorization: `Bearer ${token}` } },
		);
		if (!res.ok) return;
		const data = await res.json().catch(() => ({}));
		const d = data.Data || data;
		setRows(d.categories || []);
		setCovered(d.coveredCount || 0);
	}, [getAccessTokenSilently, year]);

	useEffect(() => {
		load();
	}, [load]);

	return (
		<Box maxWidth={950} margin="0 auto" padding={3}>
			<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
				<Typography variant="h5">Scope 3 categories</Typography>
				<Chip
					size="small"
					color="secondary"
					label={`${covered} of 15 categories covered`}
				/>
				<TextField
					label="Year"
					size="small"
					value={year}
					onChange={(e) => setYear(e.target.value)}
					sx={{ width: 100, marginLeft: "auto" }}
				/>
			</Box>
			<Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
				Your value-chain emissions across the 15 GHG Protocol Scope 3
				categories. Activity-based figures come from your logged data
				(travel, commuting, transport); spend-based figures from your
				tagged spend entries.
			</Typography>

			<Paper
				elevation={0}
				sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
			>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>#</TableCell>
							<TableCell>Category</TableCell>
							<TableCell>Activity (t CO2e)</TableCell>
							<TableCell>Spend (t CO2e)</TableCell>
							<TableCell>Total (t CO2e)</TableCell>
							<TableCell>Method</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((c) => (
							<TableRow
								key={c.id}
								sx={
									c.totalTonnes === 0
										? { opacity: 0.55 }
										: undefined
								}
							>
								<TableCell>{c.id || "—"}</TableCell>
								<TableCell>{c.name}</TableCell>
								<TableCell>{c.activityTonnes}</TableCell>
								<TableCell>{c.spendTonnes}</TableCell>
								<TableCell>{c.totalTonnes}</TableCell>
								<TableCell>
									{c.methods.length === 0 ? (
										<Chip
											size="small"
											label="no data"
											variant="outlined"
										/>
									) : (
										c.methods.map((m) => (
											<Chip
												key={m}
												size="small"
												label={m}
												sx={{ mr: 0.5 }}
											/>
										))
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Paper>
		</Box>
	);
};

export default Scope3Categories;
